import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { prototypeUrl } from "@/lib/showcase";

type PortfolioGroup = "all" | "operations" | "risk" | "growth" | "platform";
type Industry = "insurance" | "finance" | "pharma" | "public";
type IndustryFilter = "all" | Industry | "cross";

type PortfolioSolution = {
  slug: string;
  name: string;
  summary: string;
  outcome: string;
  group: Exclude<PortfolioGroup, "all">;
  signal: "accent" | "good" | "warn" | "bad";
  industries: Industry[];
};

const FILTERS: Array<{ value: PortfolioGroup; label: string }> = [
  { value: "all", label: "All" },
  { value: "operations", label: "Operations" },
  { value: "risk", label: "Risk & control" },
  { value: "growth", label: "Growth" },
  { value: "platform", label: "Platform" },
];

const INDUSTRY_FILTERS: Array<{ value: IndustryFilter; label: string }> = [
  { value: "all", label: "All sectors" },
  { value: "insurance", label: "Insurance" },
  { value: "finance", label: "Finance" },
  { value: "pharma", label: "Pharma" },
  { value: "public", label: "Public" },
  { value: "cross", label: "Cross-industry" },
];

const INDUSTRY_LABEL: Record<Industry, string> = {
  insurance: "Insurance",
  finance: "Finance",
  pharma: "Pharma",
  public: "Public",
};

const SOLUTIONS: PortfolioSolution[] = [
  { slug: "underwriting-amplifier", name: "Underwriting Amplifier", summary: "Turn a fragmented submission into one governed risk view, with routine decisions accelerated and exceptions made explicit.", outcome: "Submission → evidence → appetite decision", group: "operations", signal: "accent", industries: ["insurance"] },
  { slug: "catastrophe-intelligence", name: "Catastrophe Command Center", summary: "Coordinate a fast-moving event across exposure, claims, response capacity and executive oversight.", outcome: "Event signal → coordinated response", group: "risk", signal: "bad", industries: ["insurance"] },
  { slug: "pharmacovigilance-signal-command-center", name: "Pharmacovigilance Signal Command Center", summary: "Detect potential adverse-event safety signals, run seriousness and causality checks, and prepare governed submissions with complete lineage.", outcome: "Safety signal → triage → governed PV action", group: "risk", signal: "bad", industries: ["pharma"] },
  { slug: "motor-quote-amplifier", name: "Motor Quote Amplifier", summary: "Recognize an existing caller, pull the vehicle from the plate and decide eligibility before wasting the customer's time.", outcome: "Call → eligibility → quote or stop", group: "operations", signal: "good", industries: ["insurance"] },
  { slug: "claims-intelligence", name: "Claims Intelligence", summary: "Move a claim from first notice through policy evidence, triage, action and customer communication.", outcome: "FNOL → governed resolution", group: "operations", signal: "good", industries: ["insurance"] },
  { slug: "clinical-trial-site-activation", name: "Clinical Trial Site Activation", summary: "Turn protocol, site, regulatory and supply evidence into a governed activation decision with visible blockers, owners and portfolio impact.", outcome: "Evidence → authority → site release", group: "operations", signal: "good", industries: ["pharma"] },
  { slug: "government-emergency-intelligence", name: "Government Emergency Intelligence", summary: "Coordinate evidence, authority and accountable actions across agencies during a fast-moving public emergency.", outcome: "Signal → authority → coordinated action", group: "operations", signal: "bad", industries: ["public"] },
  { slug: "financial-crime-intelligence", name: "Financial Crime & KYC Decision Center", summary: "Turn applications, registries, screening and internal cases into a human-authorized EDD or account-activation decision.", outcome: "Evidence → ownership graph → EDD decision", group: "risk", signal: "warn", industries: ["finance"] },
  { slug: "fraud-intelligence", name: "Fraud Intelligence", summary: "Surface connected behavior across claims, parties, providers and prior events without overstating the evidence.", outcome: "Anomaly → relationship evidence", group: "risk", signal: "bad", industries: ["insurance"] },
  { slug: "siu-graph-investigator", name: "SIU Graph Investigator", summary: "Follow the shared addresses, repairers and historical claims that turn an isolated case into an investigation lead.", outcome: "Case → graph → review lead", group: "risk", signal: "warn", industries: ["insurance"] },
  { slug: "regulatory-intelligence", name: "Regulatory Intelligence", summary: "Connect obligations to controls, evidence and accountable owners before the audit request arrives.", outcome: "Obligation → control → receipt", group: "risk", signal: "accent", industries: ["finance", "insurance"] },
  { slug: "capital-intelligence", name: "Capital Intelligence", summary: "Bring exposure, reinsurance and capital implications into one decision-ready view.", outcome: "Exposure → capital action", group: "growth", signal: "warn", industries: ["finance", "insurance"] },
  { slug: "distribution-intelligence", name: "Distribution Intelligence", summary: "See channel performance, partner obligations and growth opportunities in shared business context.", outcome: "Signal → partner action", group: "growth", signal: "good", industries: ["insurance"] },
  { slug: "customer-intelligence", name: "Customer Intelligence", summary: "Give service teams grounded context and next actions without losing policy or permission boundaries.", outcome: "Intent → context → service action", group: "growth", signal: "accent", industries: ["insurance"] },
  { slug: "semantic-agent-gateway", name: "Semantic Agent Gateway", summary: "Expose one semantic API that can plan, simulate, approve and execute financial actions across existing bank systems.", outcome: "Question → plan → approved action", group: "platform", signal: "accent", industries: ["finance", "insurance"] },
  { slug: "chatbot-amplifier", name: "Chatbot Amplifier", summary: "Add grounded answers, policy controls and complete handoff context beneath an existing contact-center or CCaaS experience.", outcome: "CX question → grounded action or handoff", group: "platform", signal: "good", industries: ["insurance", "finance"] },
  { slug: "knowledge-health", name: "Knowledge Health", summary: "Keep definitions, dependencies and quality controls visible as enterprise knowledge evolves.", outcome: "Change → validation → governance", group: "platform", signal: "warn", industries: ["finance", "insurance", "pharma"] },
];

export default function PrototypePortfolio({ onContact }: { onContact: () => void }) {
  const [filter, setFilter] = useState<PortfolioGroup>("all");
  const [industry, setIndustry] = useState<IndustryFilter>("all");
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const norm = query.trim().toLowerCase();
  const visible = SOLUTIONS.filter((solution) => {
    const groupMatch = filter === "all" ? true : solution.group === filter;
    if (!groupMatch) return false;
    const inds = solution.industries || [];
    const industryMatch = industry === "all"
      ? true
      : industry === "cross"
        ? inds.length > 1
        : inds.includes(industry);
    if (!industryMatch) return false;
    if (!norm) return true;
    return `${solution.name} ${solution.summary} ${solution.outcome}`.toLowerCase().includes(norm);
  });
  const displayed = showAll ? visible : visible.slice(0, 6);

  return (
    <section id="prototypes" className="portfolio-fx">
      <div className="container">
        <div className="portfolio-head">
          <div>
            <div className="portfolio-eyebrow">Interactive portfolio</div>
            <h2>Choose the operating pressure that matters now.</h2>
          </div>
          <p>
            Each experience follows a business event from first signal through
            evidence, decision, action, and executive receipt.
          </p>
        </div>

        <div className="portfolio-filters" role="group" aria-label="Filter prototypes">
          {FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={filter === item.value ? "active" : ""}
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
            >
              {item.label}
            </button>
          ))}
          <span className="portfolio-divider" aria-hidden="true" />
          {INDUSTRY_FILTERS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={industry === item.value ? "industry active" : "industry"}
              aria-pressed={industry === item.value}
              onClick={() => setIndustry(item.value)}
            >
              {item.label}
            </button>
          ))}
          <input
            className="portfolio-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search prototypes"
            aria-label="Search prototypes"
          />
        </div>

        <div id="portfolio-results" className="portfolio-grid" aria-live="polite">
          {displayed.map((solution) => (
            <a
              key={solution.slug}
              className="portfolio-card"
              data-signal={solution.signal}
              href={prototypeUrl(solution.slug)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="portfolio-card-top">
                <span className="portfolio-index">{String(SOLUTIONS.indexOf(solution) + 1).padStart(2, "0")}</span>
                <span className="portfolio-status">Interactive simulation</span>
              </div>
              <div className="portfolio-tags">
                {solution.industries.map((item) => (
                  <span key={`${solution.slug}-${item}`} className={`portfolio-tag ${item}`}>
                    {INDUSTRY_LABEL[item]}
                  </span>
                ))}
              </div>
              <div className="portfolio-signal" />
              <h3>{solution.name}</h3>
              <p>{solution.summary}</p>
              <div className="portfolio-card-foot">
                <span>{solution.outcome}</span>
                <span className="portfolio-open" aria-hidden="true">
                  <ArrowUpRight size={17} strokeWidth={1.8} />
                </span>
              </div>
              <span className="sr-only">Open {solution.name}</span>
            </a>
          ))}
          {visible.length === 0 && (
            <p className="portfolio-empty">No experiences match these filters.</p>
          )}
        </div>

        {visible.length > 6 && (
          <div className="portfolio-expand">
            <span className="portfolio-count" aria-live="polite">
              Showing {displayed.length} of {visible.length} experiences
            </span>
            <button
              className="portfolio-expand-button"
              type="button"
              aria-expanded={showAll}
              aria-controls="portfolio-results"
              onClick={() => setShowAll((current) => !current)}
            >
              {showAll ? "Show fewer experiences" : `Show all ${visible.length} experiences`}
            </button>
          </div>
        )}

        <div className="portfolio-next">
          <div>
            <strong>Move from simulation to your operating stack.</strong>
            <span>Review the architecture, or map one governed decision with our team.</span>
          </div>
          <div className="portfolio-next-actions">
            <Link className="btn" to="/docs">Read implementation docs</Link>
            <button className="btn-primary btn" type="button" onClick={onContact}>
              Discuss your use case <ArrowRight size={15} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .portfolio-fx {
          background-color: hsl(var(--bg)); color: hsl(var(--text));
          background-image:
            linear-gradient(hsl(var(--border) / .22) 1px, transparent 1px),
            linear-gradient(90deg, hsl(var(--border) / .22) 1px, transparent 1px);
          background-size: 72px 72px;
        }
        .portfolio-fx .container { max-width: 1200px; margin: 0 auto; padding: 56px 32px; }
        .portfolio-fx .portfolio-head {
          display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(260px, .75fr);
          gap: 60px; align-items: end; padding-bottom: 28px;
          border-bottom: 1px solid hsl(var(--border));
        }
        .portfolio-fx .portfolio-eyebrow {
          display: flex; align-items: center; gap: 12px; margin-bottom: 18px;
          color: hsl(var(--accent)); font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 11px; font-weight: 500; line-height: 1.3;
          text-transform: uppercase; letter-spacing: .13em;
        }
        .portfolio-fx .portfolio-eyebrow::before { content: ""; width: 32px; height: 1px; background: currentColor; }
        .portfolio-fx .portfolio-head h2 { margin: 0; max-width: 760px; }
        .portfolio-fx .portfolio-head > p { margin: 0; color: hsl(var(--muted)); font-size: 16px; line-height: 1.65; }
        .portfolio-fx .portfolio-filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 20px 0 22px; }
        .portfolio-fx .portfolio-filters button {
          min-height: 38px; padding: 0 14px; border: 1px solid hsl(var(--border));
          border-radius: 5px; background: hsl(var(--panel)); color: hsl(var(--muted));
          cursor: pointer; font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: .04em;
        }
        .portfolio-fx .portfolio-filters button:hover,
        .portfolio-fx .portfolio-filters button.active {
          background: hsl(var(--text)); border-color: hsl(var(--text)); color: hsl(var(--bg));
        }
        .portfolio-fx .portfolio-divider { width: 1px; height: 26px; background: hsl(var(--border)); margin: 0 3px; }
        .portfolio-fx .portfolio-filters button.industry {
          min-height: 28px;
          padding: 0 11px;
          border-radius: 999px;
          background: hsl(var(--panel));
          color: hsl(var(--muted));
          font-size: 10px;
        }
        .portfolio-fx .portfolio-filters button.industry:hover,
        .portfolio-fx .portfolio-filters button.industry.active {
          border-color: hsl(var(--accent) / .45);
          background: hsl(var(--accent) / .08);
          color: hsl(var(--text));
        }
        .portfolio-fx .portfolio-search {
          margin-left: auto;
          min-height: 38px;
          min-width: min(320px, 100%);
          padding: 0 12px;
          border: 1px solid hsl(var(--border));
          border-radius: 5px;
          background: hsl(var(--panel));
          color: hsl(var(--text));
          font: 500 11px/1 'IBM Plex Mono', ui-monospace, monospace;
        }
        .portfolio-fx .portfolio-search:focus {
          outline: none;
          border-color: hsl(var(--accent));
          box-shadow: 0 0 0 3px hsl(var(--accent) / .15);
        }
        .portfolio-fx .portfolio-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 12px; }
        .portfolio-fx .portfolio-card {
          --portfolio-signal: var(--accent);
          position: relative; grid-column: span 4; min-height: 244px;
          display: flex; flex-direction: column; padding: 20px;
          border: 1px solid hsl(var(--border)); border-radius: 8px;
          background: hsl(var(--panel)); color: hsl(var(--text)); overflow: hidden;
          transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
        }
        .portfolio-fx .portfolio-card[data-signal="good"] { --portfolio-signal: var(--good); }
        .portfolio-fx .portfolio-card[data-signal="warn"] { --portfolio-signal: var(--warn); }
        .portfolio-fx .portfolio-card[data-signal="bad"] { --portfolio-signal: var(--bad); }
        .portfolio-fx .portfolio-card:hover {
          transform: translateY(-4px); border-color: hsl(var(--portfolio-signal) / .55);
          box-shadow: 0 20px 50px hsl(var(--text) / .10);
        }
        .portfolio-fx .portfolio-card::after {
          content: ""; position: absolute; right: -38px; bottom: -58px;
          width: 170px; height: 170px; border: 1px solid hsl(var(--portfolio-signal) / .28);
          border-radius: 50%; pointer-events: none;
        }
        .portfolio-fx .portfolio-card-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .portfolio-fx .portfolio-index { font: 500 11px/1 'IBM Plex Mono', ui-monospace, monospace; color: hsl(var(--muted)); }
        .portfolio-fx .portfolio-status {
          display: flex; align-items: center; gap: 7px; color: hsl(var(--muted));
          font: 500 10px/1 'IBM Plex Mono', ui-monospace, monospace;
          text-transform: uppercase; letter-spacing: .05em;
        }
        .portfolio-fx .portfolio-status::before {
          content: ""; width: 7px; height: 7px; border-radius: 50%;
          background: hsl(var(--portfolio-signal)); box-shadow: 0 0 0 4px hsl(var(--portfolio-signal) / .14);
        }
        .portfolio-fx .portfolio-tags { display: flex; gap: 6px; flex-wrap: wrap; margin: 11px 0 4px; }
        .portfolio-fx .portfolio-tag {
          display: inline-flex; align-items: center; height: 19px; padding: 0 7px;
          border-radius: 999px; border: 1px solid;
          font: 600 8.5px/1 'IBM Plex Mono', ui-monospace, monospace;
          text-transform: uppercase; letter-spacing: .03em;
        }
        .portfolio-fx .portfolio-tag.insurance { color: #086a58; background: #e8f7f3; border-color: #b8e7d9; }
        .portfolio-fx .portfolio-tag.finance { color: #214ebf; background: #ebf1ff; border-color: #c7d8ff; }
        .portfolio-fx .portfolio-tag.pharma { color: #8a3ffc; background: #f3ebff; border-color: #dec8ff; }
        .portfolio-fx .portfolio-tag.public { color: #975d02; background: #fff5df; border-color: #efd39a; }
        .portfolio-fx .portfolio-signal { width: 36px; height: 2px; margin: 22px 0 12px; background: hsl(var(--portfolio-signal)); }
        .portfolio-fx .portfolio-card h3 { max-width: 390px; margin: 0; font-size: 21px; font-weight: 650; line-height: 1.13; }
        .portfolio-fx .portfolio-card > p { max-width: 420px; margin: 10px 0 18px; color: hsl(var(--muted)); font-size: 12.5px; line-height: 1.5; }
        .portfolio-fx .portfolio-card-foot {
          display: flex; align-items: center; justify-content: space-between; gap: 20px;
          margin-top: auto; padding-top: 18px; border-top: 1px solid hsl(var(--border));
        }
        .portfolio-fx .portfolio-card-foot > span:first-child {
          max-width: 80%; color: hsl(var(--muted));
          font: 500 10px/1.35 'IBM Plex Mono', ui-monospace, monospace;
          text-transform: uppercase; letter-spacing: .04em;
        }
        .portfolio-fx .portfolio-open {
          display: grid; place-items: center; flex: 0 0 auto; width: 34px; height: 34px;
          border-radius: 50%; background: hsl(var(--text)); color: hsl(var(--bg));
        }
        .portfolio-fx .portfolio-expand {
          display: flex; align-items: center; justify-content: space-between; gap: 16px;
          margin-top: 18px; padding: 14px 0; border-top: 1px solid hsl(var(--border));
        }
        .portfolio-fx .portfolio-count { color: hsl(var(--muted)); font: 500 10px/1.4 'IBM Plex Mono', ui-monospace, monospace; }
        .portfolio-fx .portfolio-expand-button {
          min-height: 36px; padding: 0 13px; border: 1px solid hsl(var(--border));
          background: transparent; color: hsl(var(--text)); cursor: pointer;
          font: 500 10px/1 'IBM Plex Mono', ui-monospace, monospace;
          text-transform: uppercase; letter-spacing: .04em;
        }
        .portfolio-fx .portfolio-expand-button:hover { border-color: hsl(var(--accent)); color: hsl(var(--accent)); }
        .portfolio-fx .portfolio-expand-button:focus-visible { outline: 2px solid hsl(var(--accent)); outline-offset: 3px; }
        .portfolio-fx .portfolio-empty { grid-column: 1 / -1; margin: 0; padding: 24px 0; color: hsl(var(--muted)); }
        .portfolio-fx .portfolio-next {
          display: flex; align-items: center; justify-content: space-between; gap: 28px;
          margin-top: 24px; padding: 18px 0; border-top: 1px solid hsl(var(--border));
        }
        .portfolio-fx .portfolio-next > div:first-child { display: grid; gap: 5px; }
        .portfolio-fx .portfolio-next strong { font-size: 16px; }
        .portfolio-fx .portfolio-next span { color: hsl(var(--muted)); font-size: 13px; }
        .portfolio-fx .portfolio-next-actions { display: flex; flex-wrap: wrap; gap: 10px; }
        @media (max-width: 900px) {
          .portfolio-fx .portfolio-head { grid-template-columns: 1fr; gap: 24px; }
          .portfolio-fx .portfolio-search { min-width: 100%; margin-left: 0; }
          .portfolio-fx .portfolio-divider { display: none; }
          .portfolio-fx .portfolio-card { grid-column: span 6; min-height: 244px; }
        }
        @media (max-width: 620px) {
          .portfolio-fx .container { padding: 40px 16px; }
          .portfolio-fx .portfolio-filters { flex-wrap: wrap; padding-bottom: 18px; }
          .portfolio-fx .portfolio-filters button { flex: 0 0 auto; }
          .portfolio-fx .portfolio-search { min-width: 100%; }
          .portfolio-fx .portfolio-card { grid-column: 1 / -1; min-height: 220px; }
          .portfolio-fx .portfolio-next { align-items: stretch; flex-direction: column; }
          .portfolio-fx .portfolio-next-actions > * { justify-content: center; flex: 1 1 auto; }
          .portfolio-fx .portfolio-expand { align-items: flex-start; flex-direction: column; }
        }
      `}</style>
    </section>
  );
}